import type { ComponentType } from 'react';
import type { Permission } from './permissions';
import { ALL_PERMISSIONS } from './permissions';

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon?: ComponentType;
  activePaths?: readonly string[];
  /**
   * Effective permissions filter navigation. AccessRoute also checks direct
   * navigation; the backend remains authoritative for every request.
   *
   * An empty array [] means the route is visible to all authenticated users.
   */
  requiredPermissions: ReadonlyArray<Permission>;
  children?: ReadonlyArray<NavItem>;
}

export type NavigationRegistry = ReadonlyArray<NavItem>;

// Re-export for convenience in tests
export { ALL_PERMISSIONS };

// Groups organize screens; permission metadata does not replace server authorization.
export const APP_NAVIGATION: NavigationRegistry = [
  {
    id: 'masterdata',
    label: 'Business records',
    path: '/admin/masterdata',
    requiredPermissions: ['admin:masterdata:read'],
  },
  {
    id: 'order-intake',
    activePaths: ['/admin/order-intake/review'],
    label: 'Order intake',
    path: '/admin/order-intake',
    requiredPermissions: ['order-intake:read'],
  },
  {
    id: 'inventory-group',
    label: 'Inventory planning',
    path: '/admin/inventory-planning',
    requiredPermissions: [],
    children: [
      {
        id: 'inventory-planning',
        label: 'Planning workspace',
        path: '/admin/inventory-planning',
        requiredPermissions: ['inventory-planning:read'],
      },
      {
        id: 'blocked-stock',
        label: 'Blocked stock',
        path: '/admin/blocked-stock',
        requiredPermissions: ['inventory-planning:blocked-stock:read'],
      },
    ],
  },
  {
    id: 'procurement',
    label: 'Procurement',
    path: '/admin/procurement',
    requiredPermissions: ['procurement:read'],
    children: [
      {
        id: 'purchase-orders',
        label: 'Orders & forecasts',
        path: '/admin/procurement',
        requiredPermissions: ['procurement:read'],
      },
      {
        id: 'delivery-tables',
        activePaths: ['/admin/delivery-tables/changes'],
        label: 'Supplier deliveries',
        path: '/admin/delivery-tables',
        requiredPermissions: ['procurement:read', 'commercial:prices:read'],
      },
    ],
  },
  {
    id: 'shipments',
    label: 'Shipment tracking',
    path: '/admin/shipments',
    activePaths: ['/admin/shipment-operations'],
    requiredPermissions: ['shipment-tracking:read'],
  },
  {
    id: 'temperature',
    label: 'Temperature compliance',
    path: '/admin/temperature',
    requiredPermissions: ['temperature-compliance:read'],
  },
  {
    id: 'warehouse',
    label: 'Warehouse monitoring',
    path: '/admin/warehouse',
    requiredPermissions: ['warehouse-monitoring:read'],
  },
  {
    id: 'billing',
    label: 'Billing & invoices',
    path: '/admin/billing',
    requiredPermissions: [],
    children: [
      {
        id: 'billing-workspace',
        label: 'Billing workspace',
        path: '/admin/billing',
        requiredPermissions: ['billing:read'],
      },
      {
        id: 'billing-setup',
        label: 'Setup',
        path: '/admin/billing-setup',
        requiredPermissions: ['admin:masterdata:read'],
      },
    ],
  },
  {
    id: 'configuration',
    label: 'Settings & rules',
    path: '/admin/configuration',
    requiredPermissions: ['admin:masterdata:read'],
  },
  {
    id: 'administration',
    label: 'Administration',
    path: '/admin',
    requiredPermissions: [],
    children: [
      {
        id: 'users',
        label: 'Users',
        path: '/admin/users',
        requiredPermissions: ['admin:users:read'],
      },
      {
        id: 'roles',
        label: 'Roles & permissions',
        path: '/admin/roles',
        requiredPermissions: ['admin:roles:read'],
      },
      {
        id: 'audit',
        label: 'Audit log',
        path: '/admin/audit',
        requiredPermissions: ['audit:read'],
      },
    ],
  },
] as const;

export function visibleNavigation(
  items: NavigationRegistry,
  allows: (permission: Permission) => boolean
): NavItem[] {
  return items.flatMap((item) => {
    const children = item.children ? visibleNavigation(item.children, allows) : undefined;
    return item.requiredPermissions.every(allows) && (!children || children.length)
      ? [{ ...item, ...(children ? { children } : {}) }]
      : [];
  });
}
